/**
 * PrintSetupGuide — one-time OS-level configuration required to make the
 * delivery-card / manifest print calls skip the browser dialog and go
 * straight to the default printer.
 *
 * Browsers cannot silently print by design (security). The workaround is
 * to launch Chrome with `--kiosk-printing`, which suppresses the dialog
 * and uses whatever is set as the OS default printer.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BookOpen, Printer, Copy, Check } from "lucide-react";
import { useState } from "react";

const WINDOWS_TARGET = `"C:\\Program Files\\Google\\Chrome\\Application\\chrome.exe" --kiosk-printing`;
const MAC_CMD = `/Applications/Google\\ Chrome.app/Contents/MacOS/Google\\ Chrome --kiosk-printing`;

const CopyBtn = ({ text, testId }) => {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Fallback: select the text for manual copy on locked-down browsers
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1600); }
      finally { document.body.removeChild(ta); }
    }
  };
  return (
    <button
      onClick={doCopy}
      data-testid={testId}
      className={`ml-2 px-2 h-7 text-xs rounded border flex items-center gap-1 transition ${
        copied ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-neutral-300 text-neutral-700 hover:border-brand-navy"
      }`}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
};

export const PrintSetupGuide = () => {
  const doPrint = () => {
    document.body.classList.add("print-only-guide");
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove("print-only-guide"), 400);
    }, 40);
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="print-setup-guide-btn">
          <BookOpen className="h-4 w-4 mr-1.5" /> Silent-print Setup
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto print:!max-h-none print:!overflow-visible">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-brand-navy">
            <Printer className="h-5 w-5" />
            Silent-print setup — skip the print dialog
          </DialogTitle>
        </DialogHeader>

        <div className="prose prose-sm max-w-none text-neutral-800 space-y-4 pt-2">
          <p className="text-sm">
            Delivery cards, packing summaries, and delivery manifests already
            fire <code>window.print()</code> automatically. Web browsers,
            however, always show a print-preview dialog for security reasons —
            which is why you keep seeing <strong>&ldquo;Save as PDF&rdquo;</strong> as the
            destination.
          </p>
          <p className="text-sm">
            To make the print jobs go <strong>straight to the physical printer</strong>
            with zero clicks, launch Chrome with the <code>--kiosk-printing</code>
            flag. This is a one-time setup on each packing/dispatch workstation.
          </p>

          <h3 className="font-semibold text-base pt-2">Step 1 — Set the default printer (OS level)</h3>
          <ul className="text-sm list-disc pl-5 space-y-1">
            <li><strong>Windows:</strong> Settings → Bluetooth &amp; devices → Printers &amp; scanners → click your printer → <em>Set as default</em>. Turn OFF &ldquo;Let Windows manage my default printer&rdquo;.</li>
            <li><strong>macOS:</strong> System Settings → Printers &amp; Scanners → click your printer → <em>Set as default</em>.</li>
          </ul>

          <h3 className="font-semibold text-base pt-2">Step 2 — Launch Chrome with kiosk-printing</h3>
          <p className="text-sm">Right-click your existing Chrome shortcut → Properties (Windows) / Get Info (macOS), and change the Target / launch command to:</p>

          <h4 className="font-semibold text-sm mt-3">Windows shortcut Target field:</h4>
          <div className="flex items-start gap-1">
            <pre className="bg-neutral-100 border rounded p-2 text-xs whitespace-pre-wrap break-all flex-1"><code>{WINDOWS_TARGET}</code></pre>
            <CopyBtn text={WINDOWS_TARGET} testId="print-setup-copy-windows" />
          </div>
          <p className="text-xs text-neutral-600 mt-1">
            <strong>Important:</strong> In the shortcut&rsquo;s Properties &rarr; Target field, put the cursor at
            the very end of the existing path (after the closing <code>&quot;</code>), press <kbd>Space</kbd>,
            then paste <code>--kiosk-printing</code>. Don&rsquo;t overwrite the original Chrome path.
          </p>

          <h4 className="font-semibold text-sm mt-3">macOS Terminal launch:</h4>
          <div className="flex items-start gap-1">
            <pre className="bg-neutral-100 border rounded p-2 text-xs whitespace-pre-wrap break-all flex-1"><code>{MAC_CMD}</code></pre>
            <CopyBtn text={MAC_CMD} testId="print-setup-copy-mac" />
          </div>
          <p className="text-xs text-neutral-600 mt-1">
            Or make a permanent launcher via <strong>Automator</strong> &rarr; New Application &rarr;
            &ldquo;Run Shell Script&rdquo; &rarr; paste the command with <code>&amp;</code> at the end &rarr;
            Save to Applications as &ldquo;Chrome Kiosk Print&rdquo;.
          </p>

          <p className="text-sm">
            After Chrome is relaunched with this flag, every print job from the
            app will silently go to the OS default printer — no dialog, no
            &ldquo;Save as PDF&rdquo; trap. The rest of the browser (tabs, bookmarks,
            back button, DevTools) works exactly the same.
          </p>

          <h3 className="font-semibold text-base pt-2">Verifying it works</h3>
          <ol className="text-sm list-decimal pl-5 space-y-1">
            <li>Open the app in this Chrome window.</li>
            <li>Go to a fully-packed run → click <em>Delivery arrangement</em> → <em>Print manifest</em>.</li>
            <li>The printer should start printing immediately, and the print tab should auto-close within a second.</li>
          </ol>

          <h3 className="font-semibold text-base pt-2">Troubleshooting</h3>
          <ul className="text-sm list-disc pl-5 space-y-1">
            <li><strong>Dialog still shows.</strong> Chrome was not launched with <code>--kiosk-printing</code>. Close every Chrome window (check the taskbar) and reopen using the modified shortcut.</li>
            <li><strong>Wrong printer.</strong> The OS default printer is being used — set the correct one in Step 1.</li>
            <li><strong>Blank page prints.</strong> Some thermal printers need a specific paper size. In the printer&rsquo;s driver preferences set the paper size to match your receipt roll (usually 80&nbsp;mm × auto).</li>
            <li><strong>Popup blocked.</strong> Chrome must allow popups for this domain. Click the popup-blocked icon in the address bar → &ldquo;Always allow&rdquo;.</li>
          </ul>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t print:hidden">
          <Button onClick={doPrint} data-testid="print-setup-print-btn">
            <Printer className="h-4 w-4 mr-1.5" /> Print this guide
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrintSetupGuide;
